import { area, curveMonotoneX, line } from 'd3-shape';
import React from 'react';
import { View } from 'react-native';
import Svg, {
  Circle,
  Defs,
  Line,
  LinearGradient,
  Path,
  Stop,
  Text as SvgText,
} from 'react-native-svg';
import { useThemedHex } from '@/hooks/use-theme-colors';

export type ChartSeries = {
  values: number[];
  color: string;
  /** Preenche gradiente sob a linha (default: false). */
  showArea?: boolean;
  /** Ponto no último valor da série (default: false). */
  showEndDot?: boolean;
  /** Espessura da linha (default: 3 com área, 2.5 sem). */
  strokeWidth?: number;
};

type LineAreaChartProps = {
  series: ChartSeries[];
  /** Um label por índice; string vazia esconde. */
  xLabels: string[];
  height: number;
  formatY: (value: number) => string;
  /** Divisões da grade horizontal (default: 4). */
  sections?: number;
};

const PADDING_LEFT = 46;
const PADDING_RIGHT = 12;
const PADDING_TOP = 12;
const PADDING_BOTTOM = 26;

// Tokens (light-only): neutral-500 texto, neutral-200 grade.
const AXIS_TEXT = '#737373';
const GRID_COLOR = '#E5E5E5';
const AXIS_FONT_SIZE = 10;

/**
 * Gráfico de linha/área responsivo em react-native-svg + d3-shape.
 * Mede a largura via onLayout e recalcula os paths — sem o cálculo frágil de
 * `spacing` da lib antiga, então não estoura o card nem clipa o último ponto.
 */
export function LineAreaChart({
  series,
  xLabels,
  height,
  formatY,
  sections = 4,
}: LineAreaChartProps) {
  const th = useThemedHex();
  const [width, setWidth] = React.useState(0);

  const pointCount = series[0]?.values.length ?? 0;

  const { minY, maxY } = React.useMemo(() => {
    const all = series.flatMap((s) => s.values);
    const rawMax = Math.max(...all, 0);
    const rawMin = Math.min(...all, 0);
    // Folga de 20% no topo pra linha não colar na borda.
    const top = rawMax === 0 && rawMin === 0 ? 10 : rawMax * 1.2;
    const bottom = rawMin < 0 ? rawMin * 1.2 : 0;
    return { minY: bottom, maxY: top };
  }, [series]);

  const plotW = Math.max(0, width - PADDING_LEFT - PADDING_RIGHT);
  const plotH = Math.max(0, height - PADDING_TOP - PADDING_BOTTOM);

  const xAt = (index: number) => {
    if (pointCount <= 1) return PADDING_LEFT + plotW / 2;
    return PADDING_LEFT + (index / (pointCount - 1)) * plotW;
  };
  const yAt = (value: number) => {
    const span = maxY - minY || 1;
    return PADDING_TOP + plotH * (1 - (value - minY) / span);
  };

  // Linha de base da área: zero se dentro do range, senão o mínimo.
  const baseline = yAt(Math.min(Math.max(0, minY), maxY));

  const makeLine = line<number>()
    .x((_, i) => xAt(i))
    .y((v) => yAt(v))
    .curve(curveMonotoneX);

  const makeArea = area<number>()
    .x((_, i) => xAt(i))
    .y0(baseline)
    .y1((v) => yAt(v))
    .curve(curveMonotoneX);

  const gridLines = React.useMemo(() => {
    const rows: { y: number; value: number }[] = [];
    for (let i = 0; i <= sections; i++) {
      const value = minY + ((maxY - minY) * i) / sections;
      rows.push({ y: yAt(value), value });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minY, maxY, sections, plotH, height]);

  return (
    <View
      style={{ height }}
      onLayout={(e) => {
        const w = e.nativeEvent.layout.width;
        if (w > 0 && w !== width) setWidth(w);
      }}
    >
      {width > 0 && plotW > 0 && pointCount > 0 && (
        <Svg width={width} height={height}>
          <Defs>
            {series.map((s, i) =>
              s.showArea ? (
                <LinearGradient key={`grad-${i}`} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <Stop offset="0" stopColor={s.color} stopOpacity={0.18} />
                  <Stop offset="1" stopColor={s.color} stopOpacity={0} />
                </LinearGradient>
              ) : null,
            )}
          </Defs>

          {/* Grade + labels do eixo Y */}
          {gridLines.map((row, i) => (
            <React.Fragment key={`grid-${i}`}>
              <Line
                x1={PADDING_LEFT}
                y1={row.y}
                x2={width - PADDING_RIGHT}
                y2={row.y}
                stroke={th(GRID_COLOR)}
                strokeWidth={1}
              />
              <SvgText
                x={PADDING_LEFT - 8}
                y={row.y + 3}
                fontSize={AXIS_FONT_SIZE}
                fill={th(AXIS_TEXT)}
                textAnchor="end"
              >
                {formatY(row.value)}
              </SvgText>
            </React.Fragment>
          ))}

          {/* Áreas (atrás das linhas) */}
          {series.map((s, i) =>
            s.showArea ? (
              <Path
                key={`area-${i}`}
                d={makeArea(s.values) ?? undefined}
                fill={`url(#grad-${i})`}
              />
            ) : null,
          )}

          {/* Linhas */}
          {series.map((s, i) => (
            <Path
              key={`line-${i}`}
              d={makeLine(s.values) ?? undefined}
              fill="none"
              stroke={s.color}
              strokeWidth={s.strokeWidth ?? (s.showArea ? 3 : 2.5)}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* Ponto final: halo branco + preenchido, dá acabamento */}
          {series.map((s, i) =>
            s.showEndDot && s.values.length > 0 ? (
              <React.Fragment key={`dot-${i}`}>
                <Circle
                  cx={xAt(s.values.length - 1)}
                  cy={yAt(s.values[s.values.length - 1])}
                  r={5}
                  fill={th('#FFFFFF')}
                />
                <Circle
                  cx={xAt(s.values.length - 1)}
                  cy={yAt(s.values[s.values.length - 1])}
                  r={3.5}
                  fill={s.color}
                />
              </React.Fragment>
            ) : null,
          )}

          {/* Labels do eixo X */}
          {xLabels.map((label, i) =>
            label ? (
              <SvgText
                key={`x-${i}`}
                x={xAt(i)}
                y={height - 8}
                fontSize={AXIS_FONT_SIZE}
                fill={th(AXIS_TEXT)}
                textAnchor="middle"
              >
                {label}
              </SvgText>
            ) : null,
          )}
        </Svg>
      )}
    </View>
  );
}
